export const SHADOWSCAN_WORDPRESS_PLUGIN = {
  slug: "shadowscan-security-link",
  name: "ShadowScan Security Link",
  wordpressOrgUrl: "https://wordpress.org/plugins/shadowscan-security-link/",
} as const;

export type HostedPluginInstallSource = "manual" | "host_assisted";

export type HostedPluginInstallStatus =
  | "manual"
  | "host_assisted_pending"
  | "host_assisted_verified"
  | "install_failed";

export type HostedAutomationStatus =
  | "pending"
  | "provisioning"
  | "awaiting_migration_confirmation"
  | "awaiting_wordpress"
  | "awaiting_plugin"
  | "awaiting_pairing"
  | "completed"
  | "failed";

export type HostedLifecycleState =
  | "ordered"
  | "hosting_provisioned"
  | "migration_pending"
  | "wordpress_installed_or_connected"
  | "plugin_paired"
  | "guards_live";

export interface HostedLifecycleManagedSite {
  id?: string | null;
  wp_admin_url?: string | null;
  paired_at?: string | null;
  last_heartbeat_at?: string | null;
  plugin_version?: string | null;
  site_token_hash?: string | null;
  can_install_plugins?: boolean | null;
  plugin_install_source?: HostedPluginInstallSource | null;
  plugin_install_status?: HostedPluginInstallStatus | null;
  plugin_install_attempted_at?: string | null;
  plugin_install_verified_at?: string | null;
  plugin_install_error?: string | null;
  hosted_provider?: string | null;
  hosted_whmcs_client_id?: number | null;
  hosted_whmcs_service_id?: number | null;
  hosted_whmcs_order_id?: number | null;
  hosted_automation_status?: HostedAutomationStatus | null;
  hosted_automation_last_run_at?: string | null;
  hosted_automation_error?: string | null;
  hosted_is_migration?: boolean | null;
  hosted_migration_confirmed_at?: string | null;
  hosted_migration_confirmed_by?: string | null;
}

export interface HostedLifecycleDomain {
  status?: string | null;
  wp_admin_url?: string | null;
  platform?: string | null;
}

export interface HostedLifecycleSnapshot {
  state: HostedLifecycleState;
  stateIndex: number;
  pluginInstalled: boolean;
  pluginPaired: boolean;
  guardsLive: boolean;
  hasWordPressConnection: boolean;
  canInstallPlugins: boolean | null;
}

export interface HostedPluginInstallSnapshot {
  source: HostedPluginInstallSource | null;
  status: HostedPluginInstallStatus | null;
  visibleInPortal: boolean;
  label: string;
  description: string;
  tone: "neutral" | "warning" | "success" | "danger";
  attemptedAt: string | null;
  verifiedAt: string | null;
  error: string | null;
}

export interface HostedAutomationSnapshot {
  status: HostedAutomationStatus | null;
  visibleInPortal: boolean;
  label: string;
  description: string;
  tone: "neutral" | "warning" | "success" | "danger";
  lastRunAt: string | null;
  error: string | null;
}

export const HOSTED_LIFECYCLE_STEPS: Array<{
  state: HostedLifecycleState;
  label: string;
  description: string;
}> = [
  {
    state: "ordered",
    label: "Ordered",
    description: "The site entry exists and is waiting for setup handoff.",
  },
  {
    state: "hosting_provisioned",
    label: "Hosting provisioned",
    description: "Provisioning handoff is complete or the site is ready for WordPress setup.",
  },
  {
    state: "migration_pending",
    label: "Migration pending",
    description: "Billing and hosting can be active while WordPress migration is still customer-led and protection is not live yet.",
  },
  {
    state: "wordpress_installed_or_connected",
    label: "WordPress installed or connected",
    description: "A WordPress admin path is available for the site.",
  },
  {
    state: "plugin_paired",
    label: "Plugin paired",
    description: "The official ShadowScan Security Link plugin has been paired to the portal.",
  },
  {
    state: "guards_live",
    label: "Guards live",
    description: "The paired plugin is reporting heartbeats and protection sync is live.",
  },
];

export const getHostedLifecycleMeta = (state: HostedLifecycleState) =>
  HOSTED_LIFECYCLE_STEPS.find((step) => step.state === state) ?? HOSTED_LIFECYCLE_STEPS[0];

export const resolveHostedPluginInstall = (
  managedSite?: HostedLifecycleManagedSite | null,
): HostedPluginInstallSnapshot => {
  const source = managedSite?.plugin_install_source ?? null;
  const status =
    managedSite?.plugin_install_status ?? (source === "manual" ? "manual" : null);

  if (!source && !status) {
    return {
      source: null,
      status: null,
      visibleInPortal: false,
      label: "Not tracked",
      description: "Hosted install tracking is not configured for this site yet.",
      tone: "neutral",
      attemptedAt: null,
      verifiedAt: null,
      error: null,
    };
  }

  const attemptedAt = managedSite?.plugin_install_attempted_at ?? null;
  const verifiedAt = managedSite?.plugin_install_verified_at ?? null;
  const error = managedSite?.plugin_install_error ?? null;

  switch (status) {
    case "host_assisted_pending":
      return {
        source,
        status,
        visibleInPortal: true,
        label: "Host-assisted pending",
        description: "Valnex-hosted install assistance is in progress. Pairing can continue after the plugin is active.",
        tone: "warning",
        attemptedAt,
        verifiedAt,
        error,
      };
    case "host_assisted_verified":
      return {
        source,
        status,
        visibleInPortal: true,
        label: "Host-assisted verified",
        description: "The plugin install path was verified after activation, pairing, and heartbeat.",
        tone: "success",
        attemptedAt,
        verifiedAt,
        error,
      };
    case "install_failed":
      return {
        source,
        status,
        visibleInPortal: true,
        label: "Install failed",
        description: "Host-assisted installation did not complete. Use the manual plugin path until hosting support resolves it.",
        tone: "danger",
        attemptedAt,
        verifiedAt,
        error,
      };
    case "manual":
    default:
      return {
        source,
        status: "manual",
        visibleInPortal: true,
        label: "Manual install",
        description: "Install the official plugin in WordPress before pairing and the first heartbeat.",
        tone: "neutral",
        attemptedAt,
        verifiedAt,
        error,
      };
  }
};

export const resolveHostedAutomation = (
  managedSite?: HostedLifecycleManagedSite | null,
): HostedAutomationSnapshot => {
  const status = managedSite?.hosted_automation_status ?? null;
  const lastRunAt = managedSite?.hosted_automation_last_run_at ?? null;
  const error = managedSite?.hosted_automation_error ?? null;

  if (!status) {
    return {
      status: null,
      visibleInPortal: false,
      label: "Not started",
      description: "Hosted WordPress automation has not been started for this site.",
      tone: "neutral",
      lastRunAt,
      error,
    };
  }

  switch (status) {
    case "pending":
      return {
        status,
        visibleInPortal: true,
        label: "Pending",
        description: "Hosted automation is queued but has not started yet.",
        tone: "neutral",
        lastRunAt,
        error,
      };
    case "provisioning":
      return {
        status,
        visibleInPortal: true,
        label: "Provisioning",
        description: "Valnex-hosted provisioning is running before WordPress setup continues.",
        tone: "warning",
        lastRunAt,
        error,
      };
    case "awaiting_migration_confirmation":
      return {
        status,
        visibleInPortal: true,
        label: "Awaiting migration confirmation",
        description: "Hosting is provisioned, but plugin setup is paused until the migrated WordPress site is live and migration is confirmed in the portal.",
        tone: "warning",
        lastRunAt,
        error,
      };
    case "awaiting_wordpress":
      return {
        status,
        visibleInPortal: true,
        label: "Awaiting WordPress",
        description: "Hosting is provisioned and the host-side workflow still needs to confirm WordPress is installed.",
        tone: "warning",
        lastRunAt,
        error,
      };
    case "awaiting_plugin":
      return {
        status,
        visibleInPortal: true,
        label: "Awaiting Plugin",
        description: "WordPress is available and the host-side workflow still needs to install or activate the official plugin.",
        tone: "warning",
        lastRunAt,
        error,
      };
    case "awaiting_pairing":
      return {
        status,
        visibleInPortal: true,
        label: "Awaiting Pairing",
        description: "The host-side workflow reports the plugin is active and pairing still needs to complete.",
        tone: "warning",
        lastRunAt,
        error,
      };
    case "completed":
      return {
        status,
        visibleInPortal: true,
        label: "Completed",
        description: "Hosted automation reported completion. Final verification still depends on pairing and heartbeat.",
        tone: "success",
        lastRunAt,
        error,
      };
    case "failed":
      return {
        status,
        visibleInPortal: true,
        label: "Failed",
        description: "Hosted automation reported a failure and needs operator review.",
        tone: "danger",
        lastRunAt,
        error,
      };
    default:
      return {
        status,
        visibleInPortal: true,
        label: "Unknown",
        description: "Hosted automation reported an unrecognized state.",
        tone: "neutral",
        lastRunAt,
        error,
      };
  }
};

export const resolveHostedLifecycle = (
  domain?: HostedLifecycleDomain | null,
  managedSite?: HostedLifecycleManagedSite | null,
): HostedLifecycleSnapshot => {
  const pluginInstalled = Boolean(managedSite?.plugin_version);
  const pluginPaired = Boolean(managedSite?.paired_at || managedSite?.site_token_hash);
  const guardsLive = Boolean(pluginPaired && managedSite?.last_heartbeat_at);
  const hasWordPressConnection = Boolean(domain?.wp_admin_url || managedSite?.wp_admin_url);
  const canInstallPlugins =
    typeof managedSite?.can_install_plugins === "boolean" ? managedSite.can_install_plugins : null;

  let state: HostedLifecycleState = "ordered";
  if (guardsLive) {
    state = "guards_live";
  } else if (pluginPaired) {
    state = "plugin_paired";
  } else if (managedSite?.hosted_is_migration && !managedSite?.hosted_migration_confirmed_at) {
    state = "migration_pending";
  } else if (hasWordPressConnection || pluginInstalled) {
    state = "wordpress_installed_or_connected";
  } else if (domain?.status) {
    state = "hosting_provisioned";
  }

  return {
    state,
    stateIndex: HOSTED_LIFECYCLE_STEPS.findIndex((step) => step.state === state),
    pluginInstalled,
    pluginPaired,
    guardsLive,
    hasWordPressConnection,
    canInstallPlugins,
  };
};
